import { createUseStyles } from 'react-jss';
import { IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonItem, IonLabel, IonList, isPlatform } from '@ionic/react';
import { ButtonBar } from '../../components/button/ButtonBar.tsx';
import { Button } from '../../components/button/Button.tsx';
import { useSubscription } from '@autopylot-internal/state';
import { MissionTimeActionSheet } from '../action-sheets/MissionTimeActionSheet.tsx';
import { AddressDirectionsIcon, DocumentIcon, ForwardIcon, MapIcon, ShareIcon } from '../../components/icons/Icons.tsx';
import { EditableMissionState } from '../model/EditableMissionState.ts';
import { MissionOtherActionSheet } from '../action-sheets/MissionOtherActionSheet.tsx';
import { MissionStatusBadge } from './MissionStatusBadge.tsx';
import { Location } from '../../map/components/Location.tsx';
import { FlightStatusBadge } from './FlightStatusBadge.tsx';
import { ICanFly } from '@autopylot-internal/tiles-client';
import { TextInput } from '../../components/field/TextInput.tsx';
import { Color } from '../../components/common/index.ts';
import { tryAndCatch } from '../../support/helpers/tryAndCatch.ts';
import { useInstance } from '../../support/ioc/useInstance';
import { AlertPresenter } from '../../app/AlertBinder.tsx';
import { Badge } from '../../components/badge/Badge.tsx';
import { MockAuthorizationActionSheetButton } from './MockAuthorizationActionSheetButton.tsx';
import { AuthorizationsButton } from './AuthorizationsButton.tsx';
import { useEffect } from 'react';
import { TrackingService } from '../../support/tracking/TrackingService.ts';
import { LinkBasedWeatherButton } from '../../weather/WeatherButton.tsx';
import { MissionNotesButton } from '../notes/MissionNotesButton.tsx';
import { RefreshTimer } from '../../components/refresher/RefreshTimer.tsx';
import { MissionChecklistButton } from '../checklist/MissionChecklistButton.tsx';
import { InsuranceButton } from '../../insurance/InsuranceButton.tsx';
import { MissionReport } from '../../reports/MissionReport.ts';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Device } from '../../support/Device.ts';
import { OperatorState } from '../../profile/OperatorState.ts';
import { LatLngIonItem } from './LatLngIonItem.tsx';
import { RevenueCatModel } from '../../paywall/revenue-cat/RevenueCatModel.ts';
import { PaywallDialogViewModel } from '../../paywall/PaywallDialogViewModel.tsx';
import { FleetManagementState } from '../../fleet/FleetManagementState.ts';
import { AircraftPickerState } from '../../fleet/AircraftPickerState.ts';
import { AircraftInMissionDetails } from '../../fleet/AircraftInMissionDetails.tsx';

const useStyles = createUseStyles({
    details: {
        '& ion-card-title': {
            fontSize: 24
        },
        '& ion-item': {
            marginLeft: -15,
            marginRight: -15,
            '& h2': {
                fontSize: 18
            },
            '& ion-icon': {
                fontSize: '32px!important'
            }
        }
    }
});

const colorByCanFly: { [canFly in ICanFly]: Color } = {
    'can-fly': 'success',
    'can-fly-with-laanc-authorization': 'warning',
    'cannot-fly': 'danger'
};

export const MissionDetails = ({ mission }: { mission: EditableMissionState }) => {
    const classes = useStyles();
    const tracker = useInstance(TrackingService);
    const isNative = (['ios', 'android'].includes(useInstance(Device).platform));
    const missionReport = useInstance(MissionReport);
    const alert = useInstance(AlertPresenter);
    const fleetState = useInstance(FleetManagementState);
    useSubscription(() => fleetState);
    const missionFleet = fleetState.getFleetForMission(mission.state.missionId);

    const aircraftPicker = useInstance(AircraftPickerState);

    const operator = useInstance(OperatorState);
    useSubscription(() => operator);

    const { showAndLogError, showAlert } = useInstance(AlertPresenter);

    useSubscription(() => mission);

    const saveName = async (name: string) => {
        await tryAndCatch(() => mission.setName(name), (e: any) => {
            if (e.status === 409) {
                showAlert({ header: 'Cannot Save Changes', message: 'There was a conflict with updating your authorization. Please pull down on the page to refresh, then try again.' });
            } else {
                showAndLogError(e);
            }
        });
    };

    const onCancelMission = async () => {
        await tryAndCatch(mission.cancelMission, showAndLogError);
    };

    // TODO perhaps some of this belongs inside tile client
    const authorizations = mission.getMissionRadius().getLAANCAuthorizeableAdvisoryFeatures().map(feature => feature.properties);

    const airspaces = Array.from(new Set(authorizations.map(authorization => {
        const { airspaceIdent, airspaceClasses } = authorization;
        return `${airspaceIdent} | ${airspaceClasses || [].join('/')}`;
    })));

    const canFly = mission.getCanFly();
    const missionStatus = mission.getMissionStatus();
    const laancStatus = mission.getAPLAANCStatus();

    const laancIconColor = missionStatus === 'user-canceled' ? 'medium' : colorByCanFly[canFly];
    const shouldShowMissionStatus = missionStatus === 'user-canceled' || mission.getCanFly() !== 'cannot-fly';
    const shouldShowFlightStatus = missionStatus === 'active' || mission.getCanFly() === 'cannot-fly';

    const showDirections = () => {
        const address = mission.getAddress();
        const coordinates = mission.getLocation();
        let locationText: string | undefined;
        if (address) {
            const { name, street, city, state, zip } = address;
            locationText = [name, street, city, state, zip].filter(p => Boolean(p)).join(', ');
        } else {
            const { lat, lng } = coordinates;
            locationText = `${lat},${lng}`;
        }
        tracker.track('Mission Directions Requested', () => ({ address, coordinates }));
        if (isPlatform('ios')) {
            window.open(`maps://?daddr=${locationText}`, '_blank');
        } else {
            // give the button component a fraction of time to reset itself from the ripple / selected state
            setTimeout(() => {
                window.open(`https://www.google.com/maps/dir/?api=1&destination=${locationText}`, '_blank');
            }, 200);
        }
    };

    const onRefreshTimer = async () => {
        if (!mission.state.isEditingName) {
            await mission.refresh();
        }
    };
    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const paywallVM = useInstance(PaywallDialogViewModel);

    const exportMission = async () => {
        try {
            const hasPurchased = await paywallVM.requirePurchase({ triggerSource: 'mission-export' });
            if (!hasPurchased){
                return;
            }

            const pdf = await missionReport.buildPDF({
                generatedAt: new Date().toISOString(),
                operator: operator.state,
                mission: mission
            });

            if (!isNative){
                tracker.track('Mission Shared', () => ({ activityType: 'web' }));
                pdf.save();

            } else {
                const directory = Directory.Data;
                const path = 'export';

                try {
                    await Filesystem.mkdir({ directory, path });
                } catch (e) {
                    // directory already exists, purge existing files
                    try {
                        const { files } = await Filesystem.readdir({ directory, path });
                        for (const file of files) {
                            if (file.name.endsWith('.pdf')) {
                                await Filesystem.deleteFile({ directory, path: `${path}/${file.name}` });
                            }
                        }
                    } catch {
                        // ignore (will be cleaned up next time)
                    }
                }

                const fileName = `AutoPylot Report: ${mission.getName()}.pdf`.replaceAll('/', '');
                const { uri } = await Filesystem.writeFile({
                    data: pdf.toDataURIString(), // Blob is only supported on web, need a base64 string (aka datauristring)
                    path: `${path}/${fileName}`,
                    directory
                });

                try {
                    const { activityType } = await Share.share({
                        // email subject
                        title: `AutoPylot Report: ${mission.getName()}`, // email subject

                        // email body
                        text: `
                            The AutoPylot report for [Operation Name] is attached. Best, [PILOT NAME]

                            Important Notice. This document captures a snapshot of the referenced operation at the time of export. This operation may have been shared with the FAA and does not serve as the sole authorization to fly in the designated area. Airspace conditions are subject to change, and pilots should always check to confirm that the flight remains approved and complies with all FAA sUAS regulations.
                        `.split('\n').map(line => line.trim()).join('\n')
                            .replaceAll('[Operation Name]', mission.getName())
                            .replaceAll('[PILOT NAME]', operator.getName()),

                        // text, // email body
                        url: uri
                    });
                    tracker.track('Mission Shared', () => ({ activityType }));
                }
                catch(shareError: any){
                    // If the user cancelled sharing, don't throw
                    if (shareError.toString().toLowerCase().includes('share canceled')) {
                        return;
                    }
                    throw shareError;
                }
            }
        }
        catch (err: any){
            err.message = `Error while building PDF for mission (${mission.state.missionId}). ${err.message}`;
            alert.showAndLogError(err);
        }
    };

    const location = mission.getLocation()!;

    return (
        <>
            <IonCard className={classes.details}>

                <RefreshTimer getIntervalMs={mission.getAutoRefreshIntervalMs} handler={onRefreshTimer} />

                <IonCardHeader>
                    <IonCardTitle>
                        {mission.state.isEditingName && (
                            // tweak the margins so input exactly matches the positioning of the label shown when not editing
                            <form style={{ marginRight: 8, marginBottom: -10, textAlign: 'center' }} onSubmit={e => {
                                e.preventDefault();
                                const name = (e.target as any).querySelector('input').value;
                                saveName(name);
                            }}>
                                <TextInput autofocus value={mission.state.name} onBlur={value => {
                                    saveName(value);
                                }} />
                            </form>

                        )}
                        {!mission.state.isEditingName && (
                            <p data-id="mission-name" style={{ textAlign: 'center', margin: 10, padding: 0 }} onClick={() => mission.startEditingName()}>
                                {mission.getName()}
                            </p>
                        )}
                    </IonCardTitle>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 10 }}>
                        {(shouldShowMissionStatus) && (
                            <MissionStatusBadge missionStatus={missionStatus} laancStatus={laancStatus} />
                        )}
                        {shouldShowFlightStatus && (
                            <FlightStatusBadge canFly={canFly} laancStatus={laancStatus} />
                        )}
                    </div>
                </IonCardHeader>
                <IonCardContent>
                    <IonList>
                        <IonItem detail={false}>
                            <IonLabel>
                                <h2>{mission.getFormattedDate()}</h2>
                                <p>{mission.getFormattedTimes()}</p>
                            </IonLabel>
                        </IonItem>
                        <IonItem button detail={false} onClick={showDirections}>
                            <IonLabel>
                                <h2>Location</h2>
                                <p><Location mission={mission} /></p>
                                {/* NOTE: if there is no address, then this redundnantly displays the coordinates
                                    but given this is edge case, it was decided to punt on consolidating this and the next field when that happens
                                */}
                            </IonLabel>
                            <AddressDirectionsIcon slot="end" color="medium" size="medium" />
                        </IonItem>

                        <LatLngIonItem
                            location={location}
                            trackingFrom="mission detail"
                        />

                        <InsuranceButton />

                        <MissionChecklistButton mission={mission} />

                        <MissionNotesButton mission={mission} />

                        <LinkBasedWeatherButton
                            key={mission.state.refreshedAt}
                            link={mission.state.links.weatherHourly}
                            location={mission.getLocation()}
                            startTime={mission.getStartTime()}
                            endTime={mission.getEndTime()}
                            onClick={() => mission.pages.weather.go() }
                        />

                        <IonItem button detail={false} onClick={() => {
                            aircraftPicker.show(mission.state.missionId);
                        }}>
                            <IonLabel>
                                <h2>Drones</h2>
                                {!missionFleet.length &&
                                    <p>{fleetState.hasAnyAircraft() ? 'No drones assigned, select some!' : 'Add your first drone!'}</p>
                                }
                                {missionFleet.map(aircraft => (
                                    <AircraftInMissionDetails
                                        key={aircraft.aircraftId}
                                        aircraft={aircraft}
                                        mission={mission}
                                    />
                                ))}
                            </IonLabel>
                            <ForwardIcon slot="end" color="medium" size="medium" />
                        </IonItem>

                        <IonItem button detail={false} onClick={() => {
                            mission.pages.viewMap.go({ source: 'airspace' });
                        }}>
                            <IonLabel>
                                <h2>Airspace</h2>
                                <p>{airspaces.join(', ')}</p>
                            </IonLabel>
                            <ForwardIcon slot="end" color="medium" size="medium" />
                        </IonItem>
                        <AuthorizationsButton mission={mission} laancIconColor={laancIconColor} />
                        <MockAuthorizationActionSheetButton mission={mission} />
                    </IonList>

                    {/* NOTE: this should still show as rescinded, until it has ended
                                BUT we're waiting to take action until we can discuss with FAA

                        NOTE:   a mission is invalid if it is active (not canceled or completed)
                                and you cannot fly. The only appropriate action to take is to cancel the mission.
                                Since you cannot create a mission, that you cannot fly, this can only happen based on the following scenarios
                                -  one or more, or all authorizations could have been rescinded by the FAA
                                - one or more, or all authorizations could have been invalidated by FAA GIS changes (eg part time NSUFR)
                                - one or more laanc authorizations could now be inactive, or ceiling 0, or no longer allowed for your license level (107-AA vs 44809-AA)
                                - one or more laanc authorizations could now have a ceiling that is less that was authorized
                                - an area that was previosly uncontrolled, could now require laanc
                                - an area that was uncontrolled, could now be restricted (eg part time NSUFR)
                    */}
                    {mission.shouldCancel() && !mission.hasEnded() &&
                        <div style={{ textAlign: 'center' }}>
                            <div data-type="warning-panel" style={{ marginTop: 10, marginBottom: 10 }}>
                                <Badge color="danger" saturated={true}>
                                    <span style={{ fontWeight: 'normal' }}>
                                        {mission.wasUncontrolled() ? (
                                            <>No longer allowed to fly mission.</>
                                        ) : mission.getAPLAANCStatus() === 'authorizing-failed' ? (
                                            <>LAANC authorization failed. Please cancel this mission. It may still be possible to get an authorization by recreating the mission with a slightly smaller flight area.</>
                                        ) : (
                                            <><b>Action Required:</b> This mission is no longer approved and the FAA requires that you <b>cancel this mission immediately.</b></>
                                        )}
                                    </span>
                                </Badge>
                            </div>
                            <Button fill="outline" dataType="cancel-mission" onClick={onCancelMission}>Cancel Mission</Button>
                        </div>
                    }

                    {(!mission.shouldCancel() || mission.hasEnded()) &&
                        <div style={{margin: '25px 0 45px'}}>
                            <ButtonBar>
                                <Button
                                    dataType="view-mission"
                                    fill='outline'
                                    expand="block"
                                    onClick={() => mission.pages.viewMap.go({ source: 'button' })}
                                >
                                    <MapIcon />
                                    <span style={{ marginLeft: 6 }}>Mission Map</span>
                                </Button>
                                <Button
                                    expand="block"
                                    onClick={exportMission}
                                    dataType="export-mission"
                                >
                                    {isNative ? <ShareIcon /> : <DocumentIcon />}
                                    <span style={{ marginLeft: 6 }}>{isNative ? 'Share' : 'Export'}</span>
                                </Button>
                            </ButtonBar>
                        </div>
                    }

                    <MissionTimeActionSheet mission={mission} />
                    <MissionOtherActionSheet mission={mission} />
                </IonCardContent>
            </IonCard>
        </>
    );
};
